import { useState } from "react";
import { Send, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Btn, Chip, Field, Modal, Spinner } from "@/components/kit";
import { useAdminQuery } from "@/hooks/queries";
import { adminApi } from "@/lib/api";
import { fmtDateTime } from "@/lib/toto";
import { AdminHeader, AdminTable, Badge } from "@/pages/admin/AdminLayout";

const DEST = [["home", "Home"], ["premium", "Premium / Upgrade"], ["contacts", "Contacts"], ["events", "Events"], ["notifications", "Notifications"]];

function Compose({ onSent }) {
  const cats = useAdminQuery("/admin/categories").data ?? [];
  const [f, setF] = useState({ title: "", message: "", audience: "all", categoryId: "", destination: "home" });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const counts = useAdminQuery(f.categoryId ? `/admin/broadcast/audience?categoryId=${f.categoryId}` : "/admin/broadcast/audience");
  const reach = counts.data?.[f.audience];
  const catName = cats.find((c) => c.id === f.categoryId)?.name;
  const label = `${catName ? "" : "all "}${f.audience === "premium" ? "Premium users" : "users"}${catName ? ` in ${catName}` : ""}`;

  const validate = () => {
    if (!f.title.trim()) return toast.error("Add a short title for the notification.");
    if (!f.message.trim()) return toast.error("Add a message body.");
    setConfirm(true);
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/broadcast", { title: f.title.trim(), message: f.message.trim(), audience: f.audience, categoryId: f.categoryId || null, destination: f.destination });
      toast.success(`Announcement sent to ${r.recipients} ${r.recipients === 1 ? "recipient" : "recipients"}.`);
      setF({ ...f, title: "", message: "" });
      setConfirm(false);
      onSent();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="t-card p-6 space-y-4" data-testid="admin-broadcast-compose">
      <h2 className="t-section">New announcement</h2>
      <Field label="Title" maxLength={80} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} testid="broadcast-title" />
      <Field label="Message" textarea maxLength={500} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} testid="broadcast-message" />
      <div><span className="t-label">Audience</span><div className="flex gap-2">{[["all", "All users"], ["premium", "Premium only"]].map(([v, l]) => <Chip key={v} selected={f.audience === v} onClick={() => setF({ ...f, audience: v })} testid={`broadcast-audience-${v}`}>{l}</Chip>)}</div></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label><span className="t-label">Business category</span><select className="t-input" value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })} data-testid="broadcast-category"><option value="">All categories</option>{cats.filter((c) => c.status === "active").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label><span className="t-label">Opens</span><select className="t-input" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="broadcast-destination">{DEST.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      </div>
      <p className="text-[14px] text-[#475569]" data-testid="broadcast-reach">Reach: <b>{reach ?? "…"}</b> {label}</p>
      <Btn onClick={validate} testid="broadcast-send"><Send className="w-4 h-4" />Send announcement</Btn>
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Send announcement?" description={`“${f.title}” will be delivered to ${reach ?? "?"} ${label} in their in-app notification feed (and push on mobile).`} testid="broadcast-confirm-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setConfirm(false)}>Cancel</Btn><Btn loading={busy} onClick={send} testid="broadcast-confirm">Send</Btn></div>
      </Modal>
    </div>
  );
}

export default function AdminBroadcast() {
  const [page, setPage] = useState(1);
  const q = useAdminQuery(`/admin/broadcasts?page=${page}&limit=20`);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / (d.limit || 20))) : 1;
  const resend = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/broadcasts/${target.id}/resend`);
      toast.success(`"${target.title}" re-sent to ${r.recipients} recipients.`);
      setTarget(null);
      setPage(1);
      q.refetch();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <>
      <AdminHeader title="Broadcasts" sub="Push / announcement broadcast and send history." />
      <div className="grid xl:grid-cols-[minmax(0,420px)_1fr] gap-6 items-start">
        <Compose onSent={() => { setPage(1); q.refetch(); }} />
        <div>
          <h2 className="t-section mb-3">Send history</h2>
          {q.isLoading ? <Spinner /> : (
            <AdminTable testid="admin-broadcast-history" rows={d?.items ?? []} cols={[
              { label: "Title", render: (r) => <span className="block max-w-[220px] truncate"><b>{r.title}</b><br /><span className="text-[#64748B]">{r.message}</span></span> },
              { label: "Audience", render: (r) => <Badge tone={r.audience === "premium" ? "brand" : "neutral"}>{r.audience === "premium" ? "Premium" : "All"}{r.categoryName ? ` · ${r.categoryName}` : ""}</Badge> },
              { label: "Sent", render: (r) => `${r.sentCount}/${r.recipientCount}` }, { label: "Opened", render: (r) => `${r.openedCount} (${Math.round((r.openRate ?? 0) * (r.openRate <= 1 ? 100 : 1))}%)` },
              { label: "Date", render: (r) => fmtDateTime(r.createdAt) },
              { label: "", render: (r) => <button className="t-link inline-flex items-center gap-1" onClick={() => setTarget(r)} data-testid={`broadcast-resend-${r.id}`}><RotateCw className="w-4 h-4" />Resend</button> },
            ]} empty="No announcements sent yet." />
          )}
          <div className="flex items-center gap-3 mt-3 justify-end text-[14px]">
            <Btn variant="ghost" className="h-10" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn><span>Page {page} / {pages}</span><Btn variant="ghost" className="h-10" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Btn>
          </div>
        </div>
      </div>
      <Modal open={!!target} onClose={() => setTarget(null)} title="Resend announcement?" description={target ? `“${target.title}” will be sent again to the same audience.` : ""} testid="broadcast-resend-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setTarget(null)}>Cancel</Btn><Btn loading={busy} onClick={resend} testid="broadcast-resend-confirm">Resend</Btn></div>
      </Modal>
    </>
  );
}
